import { services } from "../data/services";
import { regions, regionBySlug } from "../data/regions";
import { cities, cityBySlug } from "../data/cities";
import { provinces, provinceBySlug } from "../data/provinces";
import { sectors } from "../data/sectors";
import { insights } from "../data/insights";
import {
  frCities,
  FR_SLUGS,
  FR_SERVICE_SLUGS,
  FR_SLUG_TO_EN,
  frServiceNames,
  frServiceShorts,
  frServiceFaqs,
  frServiceCityFaqs,
  frCityName,
  resolveFrSlug,
} from "../data/frContent";
import { serviceBySlug } from "../data/services";

export const SITE_URL = "https://canada-security.com";
export const BRAND = "Maze Security Solutions";
export const BRAND_ALT = "Canada Security Guards";
export const PHONE = "+1 807 907-2874";
export const PHONE_TEL = "tel:+18079072874";
export const EMAIL = "info@mazesecurity.ca";
export const EMAIL_MAILTO = "mailto:info@mazesecurity.ca";

const ORG = {
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#organization`,
  name: BRAND,
  alternateName: BRAND_ALT,
  description: "Licensed security guard services across Canada: uniformed guards, mobile patrol, event security, construction site security, concierge security and 24/7 alarm response.",
  url: SITE_URL,
  telephone: PHONE,
  email: EMAIL,
  image: `${SITE_URL}/og-image.png`,
  logo: `${SITE_URL}/favicon.png`,
  areaServed: { "@type": "Country", name: "Canada" },
};

export function resolveFlatSlug(slug) {
  for (const s of services) {
    const prefix = `${s.slug}-in-`;
    if (slug.startsWith(prefix)) {
      const rest = slug.slice(prefix.length);
      const city = cityBySlug[rest];
      if (city) return { kind: "serviceCity", service: s, city, province: provinceBySlug[city.province] };
      const prov = provinceBySlug[rest];
      if (prov) return { kind: "serviceProvince", service: s, province: prov };
      return null;
    }
  }
  if (slug.startsWith("security-guards-")) {
    const rest = slug.slice("security-guards-".length);
    if (cityBySlug[rest]) return { kind: "city", city: cityBySlug[rest], province: provinceBySlug[cityBySlug[rest].province] };
    if (provinceBySlug[rest]) return { kind: "province", province: provinceBySlug[rest] };
    return null;
  }
  return null;
}

export function getAllRoutes() {
  const routes = ["/", "/services", "/sectors", "/service-areas", "/insights", "/about", "/careers", "/contact"];
  services.forEach((s) => routes.push(`/services/${s.slug}`));
  sectors.forEach((s) => routes.push(`/sectors/${s.slug}`));
  insights.forEach((i) => routes.push(`/insights/${i.slug}`));
  provinces.forEach((p) => routes.push(`/security-guards-${p.slug}`));
  cities.forEach((c) => routes.push(`/security-guards-${c.slug}`));
  services.forEach((s) => cities.forEach((c) => routes.push(`/${s.slug}-in-${c.slug}`)));
  services.forEach((s) => provinces.forEach((p) => routes.push(`/${s.slug}-in-${p.slug}`)));
  routes.push("/regions");
  regions.forEach((r) => routes.push(`/regions/${r.slug}`));
  routes.push("/fr");
  routes.push("/fr/villes");
  frCities.forEach((fc) => routes.push(`/fr/${fc.slug}`));
  routes.push("/fr/services");
  services.forEach((s) => routes.push(`/fr/services/${FR_SERVICE_SLUGS[s.slug]}`));
  const qcCities = cities.filter((c) => c.province === "quebec");
  services.forEach((s) => qcCities.forEach((c) => routes.push(`/fr/${FR_SERVICE_SLUGS[s.slug]}-a-${c.slug}`)));
  return routes;
}

const serviceSchema = (serviceType, areaServed, path) => ({
  "@type": "Service",
  serviceType,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed,
  url: `${SITE_URL}${path}`,
});

const faqSchema = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const breadcrumb = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export function getHead(path, ctx = {}) {
  const base = { canonical: `${SITE_URL}${path === "/" ? "/" : path}` };
  if (path === "/") {
    return {
      ...base,
      title: "Security Guard Services Canada | Maze Security Solutions",
      description: "Licensed security guard services across Canada — mobile patrol, event security, construction site & concierge security in 690+ cities. 24/7 dispatch: +1 807 907-2874.",
      jsonLd: [
        { "@context": "https://schema.org", ...ORG },
        { "@context": "https://schema.org", "@type": "WebSite", name: BRAND, url: SITE_URL },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
      ],
    };
  }
  if (path === "/services") {
    return {
      ...base,
      title: "Security Services | 20 Professional Guard Services in Canada",
      description: "Explore 20 licensed security services: uniformed guards, mobile patrol, event security, fire watch, loss prevention & more across Canada. Call +1 807 907-2874.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]) }],
    };
  }
  if (path.startsWith("/services/")) {
    const s = ctx.service;
    return {
      ...base,
      alternates: [{ lang: "fr-CA", href: `${SITE_URL}/fr/services/${FR_SERVICE_SLUGS[s.slug]}` }],
      title: `${s.name} | Licensed ${s.keywords[0].replace(/\b\w/g, (m) => m.toUpperCase())} — Maze`,
      description: `${s.short} Licensed guards, 24/7 dispatch & verified reporting across Canada. Call Maze Security at +1 807 907-2874.`,
      jsonLd: [
        { "@context": "https://schema.org", ...serviceSchema(s.name, { "@type": "Country", name: "Canada" }, path) },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
        { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: s.name, path }]) },
      ],
    };
  }
  if (path === "/sectors") {
    return {
      ...base,
      title: "Industries We Protect | Security by Sector — Maze Security",
      description: "Specialized security guard programs for construction, retail, healthcare, education, hospitality, industrial & residential sectors across Canada.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Sectors", path: "/sectors" }]) }],
    };
  }
  if (path.startsWith("/sectors/")) {
    const s = ctx.sector;
    return {
      ...base,
      title: `${s.name} Security Services Canada | Maze Security`,
      description: `${s.blurb} Licensed guards & verified reporting across Canada. 24/7 dispatch: +1 807 907-2874.`,
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Sectors", path: "/sectors" }, { name: s.name, path }]) }],
    };
  }
  if (path === "/service-areas") {
    return {
      ...base,
      title: "Service Areas | Security Guards in 690+ Canadian Cities",
      description: "Maze Security serves all 13 provinces & territories and 690+ cities: Toronto, Montreal, Vancouver, Calgary, Edmonton, Ottawa, Winnipeg, Halifax & more.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }]) }],
    };
  }
  if (path === "/insights") {
    return {
      ...base,
      title: "Security Insights | Canadian Security Industry Guides — Maze",
      description: "Practical guides on security guard licensing, loss prevention, fire watch, event security and site protection across Canada.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }]) }],
    };
  }
  if (path.startsWith("/insights/")) {
    const a = ctx.article;
    return {
      ...base,
      title: `${a.title} | Maze Security Solutions`,
      description: a.excerpt,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.excerpt,
          datePublished: a.date,
          image: a.image,
          author: { "@id": `${SITE_URL}/#organization` },
          publisher: { "@id": `${SITE_URL}/#organization` },
          mainEntityOfPage: `${SITE_URL}${path}`,
        },
      ],
    };
  }
  if (path === "/about") {
    return {
      ...base,
      title: "About Maze Security Solutions | Canada Security Guards",
      description: "Maze Security Solutions — Canada Security Guards. Licensed, supervised & verified security guard services across all 13 provinces and territories.",
      jsonLd: [{ "@context": "https://schema.org", ...ORG }, { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]) }],
    };
  }
  if (path === "/careers") {
    return {
      ...base,
      title: "Security Guard Careers in Canada | Maze Security Solutions",
      description: "Apply for security guard jobs across Canada — uniformed guards, mobile patrol, concierge & event security. Licensed or in training? Apply online.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }]) }],
    };
  }
  if (path === "/contact") {
    return {
      ...base,
      title: "Contact Maze Security Solutions | 24/7 Dispatch — Canada",
      description: "Reach Maze Security's 24/7 dispatch: +1 807 907-2874 or info@mazesecurity.ca. Licensed security guard services in 690+ Canadian cities.",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Maze Security Solutions",
          url: `${SITE_URL}/contact`,
          about: { "@id": `${SITE_URL}/#organization` },
        },
        {
          "@context": "https://schema.org",
          ...ORG,
          contactPoint: {
            "@type": "ContactPoint",
            telephone: PHONE,
            email: EMAIL,
            contactType: "customer service",
            areaServed: "CA",
            availableLanguage: ["English", "French"],
          },
        },
      ],
    };
  }
  if (path === "/fr") {
    return {
      ...base,
      title: "Services de gardiens de sécurité au Canada | Maze Security Solutions",
      description: "Gardiens de sécurité licenciés partout au Canada — patrouille mobile, événements, chantiers et concierge dans 690+ villes. Répartition 24/7 : +1 807 907-2874.",
      alternates: [
        { lang: "en-CA", href: `${SITE_URL}/` },
        { lang: "x-default", href: `${SITE_URL}/` },
      ],
      jsonLd: [
        { "@context": "https://schema.org", ...ORG },
        { "@context": "https://schema.org", "@type": "WebSite", name: BRAND, url: SITE_URL, inLanguage: "fr-CA" },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
      ],
    };
  }
  if (path === "/fr/villes") {
    return {
      ...base,
      title: "Villes du Québec desservies | Maze Security Solutions",
      description: "Gardiens de sécurité licenciés par le BSP dans 99 villes du Québec : Montréal, Laval, Québec, Sherbrooke, Saguenay et plus. Répartition 24/7.",
      alternates: [
        { lang: "en-CA", href: `${SITE_URL}/service-areas` },
        { lang: "x-default", href: `${SITE_URL}/service-areas` },
      ],
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Accueil", path: "/fr" }, { name: "Villes du Québec", path: "/fr/villes" }]) }],
    };
  }
  if (path === "/regions") {
    return {
      ...base,
      title: "Security by Region | GTA, Metro Vancouver & More — Maze Security",
      description: "Multi-city security guard programs across Canada's major regions: GTA, Metro Vancouver, Greater Montreal, Northern Ontario & more. 24/7 dispatch.",
      jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Regions", path: "/regions" }]) }],
    };
  }
  if (path.startsWith("/regions/")) {
    const r = regionBySlug[path.split("/")[2]];
    if (r) {
      return {
        ...base,
        title: `Security Guards — ${r.name} | Maze Security Solutions`,
        description: `${r.blurb} Licensed guards & verified reporting. 24/7 dispatch: +1 807 907-2874.`,
        jsonLd: [
          { "@context": "https://schema.org", ...serviceSchema("Security Guard Services", { "@type": "Place", name: r.name }, path) },
          { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Regions", path: "/regions" }, { name: r.name, path }]) },
        ],
      };
    }
  }
  if (path.startsWith("/fr/")) {
    if (path === "/fr/services") {
      return {
        ...base,
        title: "Services de sécurité | 20 services professionnels — Maze Security",
        description: "Découvrez 20 services de sécurité licenciés : gardiens en uniforme, patrouille mobile, événements, surveillance incendie et plus. +1 807 907-2874.",
        alternates: [
          { lang: "en-CA", href: `${SITE_URL}/services` },
          { lang: "x-default", href: `${SITE_URL}/services` },
        ],
        jsonLd: [{ "@context": "https://schema.org", ...breadcrumb([{ name: "Accueil", path: "/" }, { name: "Services", path: "/fr/services" }]) }],
      };
    }
    if (path.startsWith("/fr/services/")) {
      const s = serviceBySlug[FR_SLUG_TO_EN[path.slice("/fr/services/".length)]];
      if (s) {
        const sn = frServiceNames[s.slug];
        return {
          ...base,
          title: `${sn} | Maze Security Solutions`,
          description: `${frServiceShorts[s.slug]} Personnel licencié, répartition 24/7 et rapports vérifiés. +1 807 907-2874.`,
          alternates: [
            { lang: "en-CA", href: `${SITE_URL}/services/${s.slug}` },
            { lang: "x-default", href: `${SITE_URL}/services/${s.slug}` },
          ],
          jsonLd: [
            { "@context": "https://schema.org", ...serviceSchema(sn, { "@type": "Country", name: "Canada" }, path) },
            { "@context": "https://schema.org", ...faqSchema(frServiceFaqs(s)) },
            { "@context": "https://schema.org", ...breadcrumb([{ name: "Accueil", path: "/" }, { name: "Services", path: "/fr/services" }, { name: sn, path }]) },
          ],
        };
      }
    }
    const fr = resolveFrSlug(path.slice(4));
    if (fr?.kind === "city") {
      const fc = fr.frCity;
      const enPath = `/security-guards-${fc.citySlug}`;
      return {
        ...base,
        title: fc.title,
        description: fc.description,
        alternates: [
          { lang: "en-CA", href: `${SITE_URL}${enPath}` },
          { lang: "x-default", href: `${SITE_URL}${enPath}` },
        ],
        jsonLd: [
          { "@context": "https://schema.org", ...serviceSchema("Services de gardiens de sécurité", { "@type": "City", name: fc.name, containedInPlace: { "@type": "AdministrativeArea", name: "Québec" } }, path) },
          { "@context": "https://schema.org", ...faqSchema(fc.faqs) },
          { "@context": "https://schema.org", ...breadcrumb([{ name: "Accueil", path: "/" }, { name: "Régions desservies", path: "/service-areas" }, { name: fc.name, path }]) },
        ],
      };
    }
    if (fr?.kind === "serviceCity") {
      const { service, city } = fr;
      const sName = frServiceNames[service.slug];
      const cName = frCityName(city.slug);
      const enPath = `/${service.slug}-in-${city.slug}`;
      return {
        ...base,
        title: `${sName} à ${cName}, QC | Maze Security`,
        description: `${frServiceShorts[service.slug]} Gardiens licenciés à ${cName}. Répartition 24/7 : +1 807 907-2874.`,
        alternates: [
          { lang: "en-CA", href: `${SITE_URL}${enPath}` },
          { lang: "x-default", href: `${SITE_URL}${enPath}` },
        ],
        jsonLd: [
          { "@context": "https://schema.org", ...serviceSchema(sName, { "@type": "City", name: cName, containedInPlace: { "@type": "AdministrativeArea", name: "Québec" } }, path) },
          { "@context": "https://schema.org", ...faqSchema(frServiceCityFaqs(service, city)) },
          { "@context": "https://schema.org", ...breadcrumb([{ name: "Accueil", path: "/" }, { name: cName, path: `/fr/${FR_SLUGS[city.slug]}` }, { name: sName, path }]) },
        ],
      };
    }
  }
  const flat = resolveFlatSlug(path.slice(1));
  if (flat?.kind === "city") {
    const { city, province } = flat;
    const alternates = FR_SLUGS[city.slug] ? [{ lang: "fr-CA", href: `${SITE_URL}/fr/${FR_SLUGS[city.slug]}` }] : undefined;
    return {
      ...base,
      alternates,
      title: `Security Guards ${city.name}, ${province.abbr} | 24/7 Licensed — Maze Security`,
      description: `Licensed security guards in ${city.name}, ${province.abbr}: mobile patrol, event, construction & concierge security. 24/7 dispatch +1 807 907-2874.`,
      jsonLd: [
        { "@context": "https://schema.org", ...serviceSchema("Security Guard Services", { "@type": "City", name: city.name, containedInPlace: { "@type": "AdministrativeArea", name: province.name } }, path) },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
        { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: `${city.name}, ${province.abbr}`, path }]) },
      ],
    };
  }
  if (flat?.kind === "province") {
    const { province } = flat;
    return {
      ...base,
      title: `Security Guard Services in ${province.name} | Maze Security Solutions`,
      description: `Licensed security guards across ${province.name}: uniformed guards, mobile patrol, event & construction security. 24/7 dispatch +1 807 907-2874.`,
      jsonLd: [
        { "@context": "https://schema.org", ...serviceSchema("Security Guard Services", { "@type": "AdministrativeArea", name: province.name }, path) },
        { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: province.name, path }]) },
      ],
    };
  }
  if (flat?.kind === "serviceProvince") {
    const { service, province } = flat;
    return {
      ...base,
      title: `${service.name} in ${province.name} | Maze Security Solutions`,
      description: `Professional ${service.name.toLowerCase()} across ${province.name}. Licensed guards, verified reporting, 24/7 dispatch. Call +1 807 907-2874.`,
      jsonLd: [
        { "@context": "https://schema.org", ...serviceSchema(service.name, { "@type": "AdministrativeArea", name: province.name }, path) },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
        { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: service.name, path: `/services/${service.slug}` }, { name: province.name, path }]) },
      ],
    };
  }
  if (flat?.kind === "serviceCity") {
    const { service, city, province } = flat;
    const alternates =
      city.province === "quebec"
        ? [{ lang: "fr-CA", href: `${SITE_URL}/fr/${FR_SERVICE_SLUGS[service.slug]}-a-${city.slug}` }]
        : undefined;
    return {
      ...base,
      alternates,
      title: `${service.name} in ${city.name}, ${province.abbr} | Maze Security`,
      description: `Professional ${service.name.toLowerCase()} in ${city.name}, ${province.abbr}. Licensed guards, verified reporting, 24/7 dispatch. Call +1 807 907-2874.`,
      jsonLd: [
        { "@context": "https://schema.org", ...serviceSchema(service.name, { "@type": "City", name: city.name, containedInPlace: { "@type": "AdministrativeArea", name: province.name } }, path) },
        { "@context": "https://schema.org", ...faqSchema(ctx.faqs || []) },
        { "@context": "https://schema.org", ...breadcrumb([{ name: "Home", path: "/" }, { name: service.name, path: `/services/${service.slug}` }, { name: city.name, path }]) },
      ],
    };
  }
  return {
    ...base,
    title: `Page Not Found | ${BRAND}`,
    description: "The page you are looking for does not exist. Explore security guard services across Canada with Maze Security Solutions.",
    jsonLd: [],
  };
}

export const LEGACY_REDIRECTS = [
  { from: "/manned-guarding", to: "/services/uniformed-security-guards" },
  { from: "/security-guard-training", to: "/careers" },
  { from: "/careers-1", to: "/careers" },
  { from: "/thunder-bay", to: "/security-guards-thunder-bay" },
  { from: "/northwest-territories", to: "/security-guards-northwest-territories" },
  { from: "/f/how-security-personnel-protect-communities", to: "/insights" },
];

export function getSitemapRoutes() {
  const majors = cities.filter((c) => c.tier === "major" || c.tier === "mid");
  const en = [
    "/", "/about", "/contact", "/careers", "/services", "/sectors", "/regions", "/insights",
    ...services.map((s) => `/services/${s.slug}`),
    ...sectors.map((s) => `/sectors/${s.slug}`),
    ...regions.map((r) => `/regions/${r.slug}`),
    ...insights.map((i) => `/insights/${i.slug}`),
    ...majors.map((c) => `/security-guards-${c.slug}`),
  ];
  const frPairs = [
    { fr: "/fr", en: "/" },
    { fr: "/fr/villes", en: "/service-areas" },
    { fr: "/fr/services", en: "/services" },
    ...services.map((s) => ({ fr: `/fr/services/${FR_SERVICE_SLUGS[s.slug]}`, en: `/services/${s.slug}` })),
    ...majors
      .filter((c) => c.province === "quebec")
      .map((c) => ({ fr: `/fr/${FR_SLUGS[c.slug]}`, en: `/security-guards-${c.slug}` })),
  ];
  return { en, frPairs };
}
